import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { selectSsoErrorMessage, useAppSelector } from "../store";
import { retrySsoLogin } from "./ssoSession";

const FALLBACK_ERROR =
  "Single Sign-On could not be completed. Please try again.";

/**
 * Displays an SSO failure message. Retry clears site cache/cookies and
 * returns to `/` so the environment start-SSO redirect runs again.
 */
export function SsoError() {
  const [searchParams] = useSearchParams();
  const storedError = useAppSelector(selectSsoErrorMessage);
  const [isRetrying, setIsRetrying] = useState(false);

  const errorMessage = useMemo(() => {
    const fromQuery =
      searchParams.get("errormsg") ?? searchParams.get("errorMsg");
    const trimmed = fromQuery != null ? String(fromQuery).trim() : "";
    if (trimmed) {
      return trimmed;
    }
    if (storedError && String(storedError).trim()) {
      return String(storedError).trim();
    }
    return FALLBACK_ERROR;
  }, [searchParams, storedError]);

  const handleRetry = async () => {
    if (isRetrying) {
      return;
    }
    setIsRetrying(true);
    try {
      await retrySsoLogin();
    } catch {
      setIsRetrying(false);
      window.location.replace("/");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
          Single Sign-On error
        </h1>
        <p
          className="mt-3 text-sm leading-relaxed text-red-700 dark:text-red-300"
          role="alert"
        >
          {errorMessage}
        </p>
        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying}
          className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-wait disabled:opacity-70 dark:bg-blue-600 dark:hover:bg-blue-500"
        >
          {isRetrying ? "Restarting sign-in…" : "Try again"}
        </button>
      </div>
    </div>
  );
}
